import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowSquareOut, CircleNotch, ArrowLeft, ShoppingCart, Check, Flag } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import RecentlyViewed, { addToRecentlyViewed } from '@/components/RecentlyViewed';
import { trackShoeView, trackAffiliateClick, trackWishlistAdd } from '@/lib/analytics';
import useStructuredData from '@/lib/useStructuredData';
import useSeoMeta from '@/lib/useSeoMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const flagEmoji = { AU: '\uD83C\uDDE6\uD83C\uDDFA', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8' };

export default function ProductPage() {
  const { shoeId } = useParams();
  const [shoe, setShoe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inWishlist, setInWishlist] = useState(false);
  const [regionFilter, setRegionFilter] = useState('ALL');
  const [reportedLinks, setReportedLinks] = useState({});
  const { user } = useAuth();
  const { format } = useCurrency();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/shoes/${shoeId}`),
      user ? axios.get(`${API}/wishlist/check/${shoeId}`, { withCredentials: true }) : Promise.resolve({ data: { in_wishlist: false } }),
    ]).then(([shoeRes, wlRes]) => {
      setShoe(shoeRes.data);
      setInWishlist(wlRes.data.in_wishlist);
      addToRecentlyViewed(shoeId);
      // Fire GA shoe_view event with brand/model/region/price for funnel analysis
      const s = shoeRes.data;
      const cheapest = (s.store_prices || []).reduce((m, p) => (!m || (p.price && p.price < m)) ? p.price : m, null);
      trackShoeView({
        shoeId: s.shoe_id,
        brand: s.brand,
        model: s.model,
        region: (s.store_prices || [])[0]?.region,
        price: cheapest,
      });
    }).finally(() => setLoading(false));
  }, [shoeId, user]);

  const toggleWishlist = async () => {
    if (!user) return;
    try {
      if (inWishlist) {
        await axios.delete(`${API}/wishlist/${shoeId}`, { withCredentials: true });
      } else {
        await axios.post(`${API}/wishlist`, { shoe_id: shoeId }, { withCredentials: true });
        // GA: only track ADDs, not removes
        trackWishlistAdd({ shoeId, brand: shoe?.brand });
      }
      setInWishlist(!inWishlist);
      // Trigger navbar wishlist count refresh
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch (e) {
      console.error('Wishlist error:', e);
      // If 401, the session may have expired — try refreshing auth
      if (e.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          // Retry
          if (inWishlist) {
            await axios.delete(`${API}/wishlist/${shoeId}`, { withCredentials: true });
          } else {
            await axios.post(`${API}/wishlist`, { shoe_id: shoeId }, { withCredentials: true });
          }
          setInWishlist(!inWishlist);
          window.dispatchEvent(new Event('wishlist-updated'));
        } catch {
          alert('Session expired. Please log out and log back in.');
        }
      }
    }
  };

  const handleBuyClick = async (storeId, productUrl, price, region, network) => {
    // GA: track affiliate click (the #1 most important event for revenue decisions)
    trackAffiliateClick({
      retailerId: storeId,
      network,
      region,
      shoeId,
      brand: shoe?.brand,
      price,
    });
    try {
      const { data } = await axios.post(`${API}/affiliate/click`, { shoe_id: shoeId, store_id: storeId }, { withCredentials: true });
      window.open(data.affiliate_url || productUrl, '_blank');
    } catch {
      window.open(productUrl, '_blank');
    }
  };

  const reportBrokenLink = async (storeId) => {
    try {
      await axios.post(`${API}/report-broken-link`, { shoe_id: shoeId, store_id: storeId, reason: 'broken_link' });
      setReportedLinks(prev => ({ ...prev, [storeId]: true }));
    } catch (e) { console.error(e); }
  };

  // ---- Product JSON-LD structured data ----
  // Google Search uses this to show rich snippets (price, stock, brand, rating)
  // in search results — typically 30-80% higher CTR vs plain blue links.
  // We construct an `AggregateOffer` because each shoe has multiple retailers.
  const structuredData = (() => {
    if (!shoe) return null;
    const validPrices = (shoe.store_prices || []).filter(p => typeof p.price === 'number' && p.price > 0);
    let offers;
    if (validPrices.length > 0) {
      const lowPrice = Math.min(...validPrices.map(p => p.price));
      const highPrice = Math.max(...validPrices.map(p => p.price));
      const currencies = [...new Set(validPrices.map(p => p.currency || 'USD'))];
      offers = {
        '@type': 'AggregateOffer',
        priceCurrency: currencies[0] || 'USD',
        lowPrice: lowPrice.toFixed(2),
        highPrice: highPrice.toFixed(2),
        offerCount: validPrices.length,
        availability: validPrices.some(p => p.in_stock !== false)
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      };
    }
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      productID: shoe.shoe_id,
      name: `${shoe.brand || ''} ${shoe.model || ''}`.trim(),
      description: shoe.description || `${shoe.brand} ${shoe.model} — compare prices and find your perfect fit on SoleFinder.`,
      brand: shoe.brand ? { '@type': 'Brand', name: shoe.brand } : undefined,
      image: shoe.image_url || undefined,
      category: shoe.activity || shoe.category || 'Footwear',
      url: typeof window !== 'undefined' ? `${window.location.origin}/shoe/${shoe.shoe_id}` : `https://solefinder.app/shoe/${shoe.shoe_id}`,
      offers,
    };
  })();
  useStructuredData(structuredData, `product-${shoeId}`);

  // ---- SEO meta tags per shoe ----
  // Each indexed shoe page gets a unique <title> and description, so Google
  // search results show distinct, keyword-rich snippets per product instead
  // of the generic homepage one.
  useSeoMeta(
    shoe
      ? {
          title: `${shoe.brand || ''} ${shoe.model || ''} — Compare Prices · SoleFinder`.trim(),
          description: `Compare live prices for the ${shoe.brand} ${shoe.model} across AU, UK and US retailers. ${shoe.description ? shoe.description.slice(0, 100) : 'Find your perfect fit on SoleFinder.'}`,
          canonical: typeof window !== 'undefined' ? `${window.location.origin}/shoe/${shoe.shoe_id}` : `https://solefinder.app/shoe/${shoe.shoe_id}`,
          ogImage: shoe.image_url,
        }
      : {}
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>;
  if (!shoe) return <div className="min-h-screen flex items-center justify-center"><p>Shoe not found</p></div>;

  const prices = shoe.store_prices?.filter(p => {
    if (regionFilter !== 'ALL' && p.store_region !== regionFilter) return false;
    return true;
  }).sort((a, b) => a.price - b.price) || [];

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <Link to="/browse" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] mb-6" data-testid="back-link">
          <ArrowLeft size={16} /> Back to Browse
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Image */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="border border-[#E4E4E7] bg-[#F4F4F5]">
              <img src={shoe.image_url} alt={`${shoe.brand} ${shoe.model}`} className="w-full aspect-square object-cover" data-testid="product-image"
                onError={(e) => { e.target.src = `https://placehold.co/600x600/F4F4F5/52525B?text=${encodeURIComponent(shoe.brand + '\n' + shoe.model)}`; }} />
            </div>
          </motion.div>

          {/* Details */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase mt-1" data-testid="product-name">{shoe.model}</h1>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mt-2">{shoe.category}</p>

            <p className="mt-4 text-[#52525B] font-medium leading-relaxed" data-testid="product-description">{shoe.description}</p>

            {/* Features */}
            <div className="mt-6">
              <h3 className="font-heading font-bold text-lg mb-3">Features</h3>
              <div className="flex flex-wrap gap-2" data-testid="product-features">
                {shoe.features?.map((f, i) => (
                  <span key={i} className="bg-[#F4F4F5] border border-[#E4E4E7] px-3 py-1.5 text-xs font-bold uppercase tracking-wide">{f}</span>
                ))}
              </div>
            </div>

            {/* Fit Info */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="product-specs">
              <div className="border border-[#E4E4E7] p-3">
                <p className="text-xs text-[#52525B] uppercase font-bold">Fit Type</p>
                <p className="font-bold capitalize">{shoe.fit_type}</p>
              </div>
              <div className="border border-[#E4E4E7] p-3">
                <p className="text-xs text-[#52525B] uppercase font-bold">Gender</p>
                <p className="font-bold capitalize">{shoe.gender}</p>
              </div>
              <div className="border border-[#E4E4E7] p-3">
                <p className="text-xs text-[#52525B] uppercase font-bold">Category</p>
                <p className="font-bold capitalize">{shoe.category?.replace('_', ' ')}</p>
              </div>
              <div className="border border-[#E4E4E7] p-3">
                <p className="text-xs text-[#52525B] uppercase font-bold">Width Fitting</p>
                <p className="font-bold">{
                  shoe.fit_type === '2E' ? '2E — Wide' :
                  shoe.fit_type === 'B' ? 'B — Narrow' :
                  shoe.fit_type === 'D' ? 'D — Standard' :
                  shoe.fit_type === '3E' ? '3E — Extra Wide' :
                  shoe.fit_type || 'Standard'
                }</p>
              </div>
              <div className="border border-[#E4E4E7] p-3">
                <p className="text-xs text-[#52525B] uppercase font-bold">Sizes Available</p>
                <p className="font-bold">{
                  shoe.gender === 'kids' ? 'US 1–6 (Kids)' :
                  shoe.gender === 'womens' ? 'US 5–12 (Women)' :
                  shoe.gender === 'mens' ? 'US 7–14 (Men)' :
                  'US 5–14 (Unisex)'
                }</p>
              </div>
              <div className="border border-[#E4E4E7] p-3">
                <p className="text-xs text-[#52525B] uppercase font-bold">Upper Material</p>
                <p className="font-bold">{
                  (() => {
                    const feats = (shoe.features || []).map(f => f.toLowerCase()).join(' ');
                    const desc = (shoe.description || '').toLowerCase();
                    const all = feats + ' ' + desc;
                    if (all.includes('full-grain leather') || all.includes('full grain leather')) return 'Full-Grain Leather';
                    if (all.includes('patent leather') || all.includes('patent')) return 'Patent Leather';
                    if (all.includes('suede')) return 'Suede';
                    if (all.includes('kangaroo leather')) return 'Kangaroo Leather';
                    if (all.includes('calfskin') || all.includes('calf leather')) return 'Calfskin Leather';
                    if (all.includes('nubuck')) return 'Nubuck Leather';
                    if (all.includes('leather upper') || all.includes('leather') || all.includes('premium leather')) return 'Leather';
                    if (all.includes('synthetic leather') || all.includes('synthetic upper') || all.includes('faux leather')) return 'Synthetic Leather';
                    if (all.includes('primeknit') || all.includes('flyknit') || all.includes('knit upper') || all.includes('stitchlite')) return 'Engineered Knit';
                    if (all.includes('canvas')) return 'Canvas';
                    if (all.includes('mesh') || all.includes('engineered mesh')) return 'Mesh';
                    if (all.includes('textile')) return 'Textile';
                    if (all.includes('rubber') && all.includes('upper')) return 'Rubber';
                    if (all.includes('satin')) return 'Satin';
                    if (all.includes('wool felt')) return 'Wool Felt';
                    return 'Synthetic';
                  })()
                }</p>
              </div>
            </div>

            {/* Available Colors */}
            {shoe.available_colors?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-[#52525B] uppercase font-bold mb-2">Available Colours</p>
                <div className="flex gap-2 flex-wrap" data-testid="product-colors">
                  {shoe.available_colors.map(c => (
                    <span key={c} className="flex items-center gap-1.5 bg-[#F4F4F5] border border-[#E4E4E7] px-3 py-1.5 text-xs font-bold uppercase tracking-wide">
                      <span className="w-3 h-3 rounded-full border border-[#D4D4D8]" style={{ backgroundColor:
                        c === 'black' ? '#0A0A0A' : c === 'white' ? '#FFFFFF' : c === 'grey' ? '#9CA3AF' :
                        c === 'brown' ? '#92400E' : c === 'blue' ? '#3B82F6' : c === 'red' ? '#EF4444' :
                        c === 'green' ? '#22C55E' : c === 'pink' ? '#EC4899' : c === 'navy' ? '#1E3A5F' :
                        c === 'nude' ? '#D4A982' : c === 'yellow' ? '#EAB308' : c === 'orange' ? '#F97316' :
                        c === 'purple' ? '#A855F7' : c === 'gold' ? '#D4A017' : '#9CA3AF'
                      }} />
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Closure Type */}
            {(() => {
              const feats = (shoe.features || []).map(f => f.toLowerCase()).join(' ');
              const desc = (shoe.description || '').toLowerCase();
              const all = feats + ' ' + desc;
              const closure =
                all.includes('lace-up') || all.includes('lace up') || all.includes('lacing') ? 'Lace-Up' :
                all.includes('velcro') || all.includes('hook & loop') || all.includes('hook and loop') || all.includes('strap closure') ? 'Velcro / Strap' :
                all.includes('slip-on') || all.includes('slip on') || all.includes('pull-on') || all.includes('pull on') ? 'Slip-On / Pull-On' :
                all.includes('buckle') ? 'Buckle' :
                all.includes('zip') || all.includes('zipper') ? 'Zip' :
                all.includes('elastic') || all.includes('chelsea') ? 'Elastic / Chelsea' :
                all.includes('t-bar') || all.includes('mary jane') ? 'T-Bar / Mary Jane' :
                null;
              return closure ? (
                <div className="mt-3 inline-flex items-center gap-2 bg-[#F4F4F5] border border-[#E4E4E7] px-3 py-1.5">
                  <span className="text-xs text-[#52525B] uppercase font-bold">Closure:</span>
                  <span className="text-xs font-bold uppercase">{closure}</span>
                </div>
              ) : null;
            })()}

            {/* Dimensions */}
            {shoe.dimensions && Object.keys(shoe.dimensions).length > 0 && (
              <div className="mt-6" data-testid="product-dimensions">
                <h3 className="font-heading font-bold text-lg mb-3">Dimensions</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {shoe.dimensions.weight_g && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Weight</p>
                      <p className="font-bold">{shoe.dimensions.weight_g}g</p>
                    </div>
                  )}
                  {shoe.dimensions.drop_mm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Heel-Toe Drop</p>
                      <p className="font-bold">{shoe.dimensions.drop_mm}mm</p>
                    </div>
                  )}
                  {shoe.dimensions.stack_height_mm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Stack Height</p>
                      <p className="font-bold">{shoe.dimensions.stack_height_mm}mm</p>
                    </div>
                  )}
                  {shoe.dimensions.shaft_height_cm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Shaft Height</p>
                      <p className="font-bold">{shoe.dimensions.shaft_height_cm}cm</p>
                    </div>
                  )}
                  {shoe.dimensions.platform_height_mm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Platform Height</p>
                      <p className="font-bold">{shoe.dimensions.platform_height_mm}mm</p>
                    </div>
                  )}
                  {shoe.dimensions.heel_height_mm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Heel Height</p>
                      <p className="font-bold">{shoe.dimensions.heel_height_mm}mm</p>
                    </div>
                  )}
                  {shoe.dimensions.sole_height_mm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Sole Height</p>
                      <p className="font-bold">{shoe.dimensions.sole_height_mm}mm</p>
                    </div>
                  )}
                  {shoe.dimensions.footbed_depth_mm && (
                    <div className="border border-[#E4E4E7] p-3">
                      <p className="text-xs text-[#52525B] uppercase font-bold">Footbed Depth</p>
                      <p className="font-bold">{shoe.dimensions.footbed_depth_mm}mm</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Wishlist Button */}
            {user ? (
              <button onClick={toggleWishlist} className={`mt-6 w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wide px-8 py-4 transition-colors border-2 ${inWishlist ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#F4F4F5]'}`} data-testid="add-to-wishlist-btn">
                <Heart size={20} weight={inWishlist ? 'fill' : 'regular'} />
                {inWishlist ? 'In Wishlist' : 'Add to Wishlist'}
              </button>
            ) : (
              <Link to="/login" className="mt-6 w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wide px-8 py-4 transition-colors border-2 border-[#E4E4E7] text-[#52525B] hover:border-[#0A0A0A] hover:text-[#0A0A0A]" data-testid="login-to-wishlist-btn">
                <Heart size={20} weight="regular" /> Sign in to Save
              </Link>
            )}
          </motion.div>
        </div>

        {/* Store Price Comparison */}
        <motion.div className="mt-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h2 className="font-heading font-bold text-2xl tracking-tight uppercase" data-testid="store-comparison-title">Store Prices</h2>
            <div className="flex gap-2" data-testid="product-region-filter">
              {['ALL', 'AU', 'UK', 'US'].map(r => (
                <button key={r} onClick={() => setRegionFilter(r)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${regionFilter === r ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}>
                  {r === 'ALL' ? 'All' : `${flagEmoji[r]} ${r}`}
                </button>
              ))}
            </div>
          </div>

          {prices.length > 0 ? (
            <div className="border border-[#E4E4E7]">
              {/* Header */}
              <div className="hidden md:grid grid-cols-5 gap-4 p-4 bg-[#F4F4F5] text-xs font-bold uppercase tracking-wide text-[#52525B] border-b border-[#E4E4E7]">
                <span>Store</span><span>Region</span><span>Price</span><span>Stock</span><span className="text-right">Action</span>
              </div>
              {prices.map((p, i) => (
                <div key={`${p.store_id}-${i}`} className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 border-b border-[#E4E4E7] last:border-b-0 items-center hover:bg-[#F4F4F5] transition-colors" data-testid={`store-row-${p.store_id}`}>
                  <span className="font-bold text-sm" data-testid={`store-name-${p.store_id}`}>{p.store_name}</span>
                  <span className="text-sm">{flagEmoji[p.store_region]} {p.store_region}</span>
                  <span className="font-bold text-lg" data-testid={`store-price-${p.store_id}`}>{format(p.price, p.currency)}</span>
                  <span className="text-sm">{p.in_stock ? <span className="text-[#10B981] flex items-center gap-1"><Check size={14} /> In Stock</span> : <span className="text-[#F59E0B]">Low Stock</span>}</span>
                  <div className="text-right col-span-2 md:col-span-1 flex items-center justify-end gap-2">
                    <button onClick={() => handleBuyClick(p.store_id, p.product_url, p.price, p.region, p.affiliate_network)}
                      className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-4 py-2 text-xs hover:bg-[#D62828] transition-colors"
                      data-testid={`buy-btn-${p.store_id}`}>
                      <ShoppingCart size={14} /> Shop <ArrowSquareOut size={14} />
                    </button>
                    {reportedLinks[p.store_id] ? (
                      <span className="text-[10px] text-[#10B981] font-bold uppercase" data-testid={`reported-${p.store_id}`}>Reported</span>
                    ) : (
                      <button onClick={() => reportBrokenLink(p.store_id)}
                        className="p-2 text-[#A1A1AA] hover:text-[#FF3B30] transition-colors" title="Report broken link"
                        data-testid={`report-btn-${p.store_id}`}>
                        <Flag size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-[#E4E4E7] p-8 text-center">
              <p className="text-[#52525B]">No stores found for this region. Try a different filter.</p>
            </div>
          )}
          <p className="text-[10px] text-[#A1A1AA] mt-3 leading-relaxed" data-testid="price-disclaimer">
            Prices shown are indicative only and subject to change without notice. Actual prices may vary due to promotions, currency fluctuations, or retailer updates. Please verify the final price on the retailer's website before purchasing. SoleFinder is not responsible for any price discrepancies.
          </p>

          {/* Also search on Amazon — honest CTA, no fake price */}
          {shoe.amazon_search && Object.keys(shoe.amazon_search).length > 0 && (
            <div className="mt-10 relative border-2 border-[#FF3B30] bg-white shadow-[6px_6px_0_0_#0A0A0A] p-5 sm:p-6" data-testid="amazon-search-section">
              {/* Corner ribbon */}
              <div className="absolute -top-3 left-5 bg-[#FF3B30] text-white text-[10px] font-black uppercase tracking-[0.18em] px-3 py-1" data-testid="amazon-section-ribbon">
                Bonus
              </div>
              <div className="flex items-start justify-between mb-4 flex-wrap gap-3 pt-1">
                <div>
                  <p className="font-heading font-black text-lg sm:text-xl tracking-tight uppercase text-[#0A0A0A]">
                    Also search on <span className="text-[#FF9900]">Amazon</span>
                  </p>
                  <p className="text-xs text-[#52525B] mt-1 max-w-md">
                    Can't find it above? Search Amazon in your region — prices vary, confirm on Amazon.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="amazon-search-buttons">
                {['AU', 'UK', 'US'].map(r => {
                  if (regionFilter !== 'ALL' && regionFilter !== r) return null;
                  const url = shoe.amazon_search[r];
                  if (!url) return null;
                  return (
                    <a key={r} href={url} target="_blank" rel="nofollow sponsored noopener noreferrer"
                      onClick={() => trackAffiliateClick({ retailerId: `amazon-${r.toLowerCase()}`, network: 'amazon_associates', region: r, shoeId, brand: shoe?.brand })}
                      className="group flex items-center justify-center gap-2 bg-[#FF9900] hover:bg-[#E68A00] text-[#0A0A0A] font-bold uppercase tracking-wide px-4 py-3.5 text-sm border-2 border-[#0A0A0A] hover:shadow-[3px_3px_0_0_#FF3B30] transition-all"
                      data-testid={`amazon-search-btn-${r}`}>
                      <span className="text-base leading-none">{flagEmoji[r]}</span>
                      <span>Amazon {r}</span>
                      <ArrowSquareOut size={15} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>

        {/* Recently Viewed */}
        <RecentlyViewed excludeId={shoeId} maxShow={6} />
      </div>
    </div>
  );
}
